import {
  CrownOutlined,
  FireOutlined,
  HeartOutlined,
  RocketOutlined,
  SmileOutlined,
  StarOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useModel } from '@umijs/max';
import { Avatar } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { getAccessToken } from '@/utils/auth';
import { buildApiUrl } from '@/utils/api';

export type AvatarIconKey =
  | 'user'
  | 'smile'
  | 'crown'
  | 'rocket'
  | 'heart'
  | 'star'
  | 'thunder'
  | 'fire';

export const AVATAR_ICON_ITEMS: Array<{ key: AvatarIconKey; icon: React.ReactNode; label: string }> = [
  { key: 'user', icon: <UserOutlined />, label: '用户' },
  { key: 'smile', icon: <SmileOutlined />, label: '微笑' },
  { key: 'crown', icon: <CrownOutlined />, label: '皇冠' },
  { key: 'rocket', icon: <RocketOutlined />, label: '火箭' },
  { key: 'heart', icon: <HeartOutlined />, label: '爱心' },
  { key: 'star', icon: <StarOutlined />, label: '星星' },
  { key: 'thunder', icon: <ThunderboltOutlined />, label: '闪电' },
  { key: 'fire', icon: <FireOutlined />, label: '火焰' },
];

const iconMap = new Map<AvatarIconKey, React.ReactNode>(
  AVATAR_ICON_ITEMS.map((i) => [i.key, i.icon]),
);

export function isIconAvatar(avatar?: string | null): avatar is `icon:${AvatarIconKey}` {
  return typeof avatar === 'string' && avatar.startsWith('icon:');
}

export function isAttachmentAvatar(avatar?: string | null): avatar is `att:${number}` {
  return typeof avatar === 'string' && avatar.startsWith('att:');
}

export type UserAvatarProps = {
  size?: number | 'small' | 'default' | 'large';
  style?: React.CSSProperties;
};

const UserAvatar: React.FC<UserAvatarProps> = ({ size = 'small', style }) => {
  const { initialState } = useModel('@@initialState');
  const avatar = initialState?.currentUser?.avatar;
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  const resolvedIcon = useMemo(() => {
    if (!isIconAvatar(avatar)) return null;
    const key = avatar.replace(/^icon:/, '') as AvatarIconKey;
    return iconMap.get(key) || null;
  }, [avatar]);

  useEffect(() => {
    let active = true;
    let lastUrl: string | null = null;

    const run = async () => {
      if (!isAttachmentAvatar(avatar)) {
        setBlobUrl(null);
        return;
      }
      const idStr = avatar.replace(/^att:/, '');
      const attachmentId = Number(idStr);
      if (!Number.isFinite(attachmentId) || attachmentId <= 0) {
        setBlobUrl(null);
        return;
      }
      const token = getAccessToken();
      const res = await fetch(buildApiUrl(`/api/users/avatar/${attachmentId}`), {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) {
        setBlobUrl(null);
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      lastUrl = url;
      if (active) setBlobUrl(url);
    };

    run();

    return () => {
      active = false;
      if (lastUrl) window.URL.revokeObjectURL(lastUrl);
    };
  }, [avatar]);

  if (resolvedIcon) {
    return <Avatar size={size} icon={resolvedIcon} style={style} />;
  }

  if (blobUrl) {
    return <Avatar size={size} src={blobUrl} style={style} />;
  }

  if (typeof avatar === 'string' && avatar) {
    return <Avatar size={size} src={avatar} style={style} />;
  }

  return <Avatar size={size} src="/images/avatar-default.png" style={style} />;
};

export default UserAvatar;

